import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CustomerV2T } from "../../types";
import { formatDate } from "../../utils/helper";

// Types
interface CustomerDetails {
  name: string;
  registerContactNumber: string;
  location: string;
  accountStatus: string;
  lastCommonTime: string;
  billingCycle: string;
  lastPaymentDate: string;
  outStandingBalance: string;
  email: string;
  customerCode: string;
  additionalInfo?: Record<string, any>;
  isActive?: boolean;
}

interface CustomerPayload {
  name?: string;
  phone?: string;
  city?: string;
  state?: string;
  country?: string;
  address?: string;
  zip?: string;
  isActive?: boolean;
  billingPreference?: string;
  createdAt?: string;
  updatedAt?: string;
  email?: string;
  customerCode?: string;
  additionalInfo?: Record<string, any>;
}

interface CustomerState {
  hasSearched: boolean;
  isCustomerLoading: boolean;
  customerId: string;
  customerDetails: CustomerDetails;
  customerFullDetails: Record<string, any>;
  customerV2Details: CustomerV2T | null;
}

// initial states
const initialState: CustomerState = {
  hasSearched: false,
  isCustomerLoading: false,
  customerId: "",
  customerDetails: {
    name: "",
    registerContactNumber: "",
    location: "",
    accountStatus: "",
    lastCommonTime: "",
    billingCycle: "",
    lastPaymentDate: "",
    outStandingBalance: "",
    customerCode: "",
    email: "",
    isActive: true,
    additionalInfo: {},
  },
  customerFullDetails: {},
  customerV2Details: null,
};

const reducers = {
  setCustomerDetails: (
    state: CustomerState,
    action: PayloadAction<CustomerPayload>,
  ) => {
    const {
      name,
      phone,
      city,
      state: region,
      country,
      address: streetAddress,
      zip,
      isActive,
      billingPreference,
      createdAt,
      updatedAt,
      email,
      customerCode,
      additionalInfo,
    } = action.payload;

    const getAddress = (
      address?: string,
      city?: string,
      region?: string,
      zip?: string,
      country?: string,
    ): string => {
      let parts = [address, city, region, zip, country].filter(Boolean);
      return parts.join(", ") || "-";
    };

    state.customerFullDetails = action.payload;
    state.customerDetails.name = name || "John Smith";
    state.customerDetails.registerContactNumber = phone || "99602 09876";
    state.customerDetails.location =
      getAddress(streetAddress, city, region, zip, country) ||
      "Kharadi, Pune, India";
    state.customerDetails.accountStatus =
      isActive !== undefined ? (isActive ? "Active" : "Inactive") : "Active";
    state.customerDetails.lastCommonTime = updatedAt
      ? formatDate(new Date(updatedAt), "DD MMM YY hh:mm a")
      : "18 Sep '24 05:55 pm";
    state.customerDetails.billingCycle = "01 Jan 24";
    state.customerDetails.lastPaymentDate = "15 Sep 24";
    state.customerDetails.outStandingBalance = "1,200";
    state.customerDetails.email = email || "";
    state.customerDetails.customerCode = customerCode || "";
    state.customerDetails.additionalInfo = {
      ...(additionalInfo || {}),
      consumerBillingType:
        billingPreference || additionalInfo?.consumerBillingType,
    };
  },
  setHasSearched: (state: CustomerState, action: PayloadAction<boolean>) => {
    state.hasSearched = action.payload;
  },
  setCustomerId: (state: CustomerState, action: PayloadAction<string>) => {
    state.customerId = action.payload;
  },
  setIsCustomerLoading: (
    state: CustomerState,
    action: PayloadAction<boolean>,
  ) => {
    state.isCustomerLoading = action.payload;
  },
  setCustomerV2Details: (
    state: CustomerState,
    action: PayloadAction<CustomerV2T | null>,
  ) => {
    state.customerV2Details = action.payload;
  },
};

// creating customer slice
const CustomerSlice = createSlice({
  name: "customer",
  initialState,
  reducers,
});

// exporting actions
export const {
  setCustomerDetails,
  setHasSearched,
  setCustomerId,
  setIsCustomerLoading,
  setCustomerV2Details,
} = CustomerSlice.actions;

export default CustomerSlice.reducer;
